// /staff — the Agent Services portal.
//
// Separate from /admin on purpose. Agent Services signs in with their own
// account (role `agent_services`) and gets exactly two things: what a recruit was
// SENT, and a read-only preview of what that recruit SEES. They deliberately do
// not get the retry / archive / send-recap controls, which fire irreversible
// writes — Brokermint user creation has no delete endpoint.
//
// The preview is enforced read-only on the SERVER (lib/auth.js
// blockReadOnlyWrites), not here. Nothing in this file is load-bearing for
// safety; it is a door, not a lock.

import React, { useState, useEffect, useCallback } from "react";
import { api, ApiError } from "../lib/api.js";
import { EPLogo, IcArrow, IcCheck } from "../components/shared.jsx";

const CARD = { background: "var(--ep-bg-elevated)", border: "1px solid var(--ep-border)",
  padding: "20px 22px", marginBottom: 14 };
const F = { display: "block", width: "100%", padding: "12px 14px", marginBottom: 14,
  background: "var(--ep-saguaro-blossom)", border: "1px solid var(--ep-border)",
  color: "var(--ep-origin)", font: "400 15px/1.4 var(--ep-font-body)", boxSizing: "border-box" };

export default function Staff() {
  const [me, setMe] = useState(null);
  const [checking, setChecking] = useState(true);

  const load = useCallback(async () => {
    try { setMe(await api.get("/staff/me")); }
    catch { setMe(null); }
    finally { setChecking(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (checking) return <Shell><p style={{ color: "var(--ep-fg-muted)" }}>Checking your sign-in…</p></Shell>;
  if (!me) return <Shell><Login onAuthed={load} /></Shell>;
  return <Shell me={me} onSignOut={() => setMe(null)}><Pipeline /></Shell>;
}

function Shell({ children, me, onSignOut }) {
  // Both roles reach this page, and an admin arriving here is not signing in
  // again — the same ep_admin cookie satisfies requireStaff and requireAdmin
  // alike. So an admin gets a way back rather than a dead end.
  const isAdmin = me?.role === "admin";
  return (
    <div style={{ minHeight: "100vh", background: "var(--ep-saguaro-blossom)",
      fontFamily: "var(--ep-font-body)", padding: "40px 20px" }}>
      <div style={{ maxWidth: 960, margin: "0 auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <EPLogo height={24} dark />
          {me && (
            <span style={{ display: "flex", alignItems: "center", gap: 14, fontSize: 12, color: "var(--ep-fg-muted)" }}>
              <span>{me.email} · {me.role}</span>
              {isAdmin && <a href="/admin" style={{ color: "var(--ep-sunset)" }}>Admin console</a>}
              <button onClick={async () => {
                await api.post("/admin/logout", {}).catch(() => {});
                onSignOut?.();
              }} style={{ background: "none", border: "none", padding: 0, cursor: "pointer",
                color: "var(--ep-sunset)", font: "inherit" }}>Sign out</button>
            </span>
          )}
        </div>
        <div className="ep-eyebrow" style={{ color: "var(--ep-sunset)", marginBottom: 28 }}>
          Agent Services
        </div>
        {children}
      </div>
    </div>
  );
}

function Login({ onAuthed }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  async function submit(e) {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      await api.post("/admin/login", { email, password });
      await onAuthed();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Sign-in failed");
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} style={{ ...CARD, maxWidth: 380 }}>
      <h1 style={{ font: "300 28px/1.1 var(--ep-font-display)", margin: "0 0 18px" }}>Sign in</h1>
      <input style={F} type="email" placeholder="you@laughtonteam.com" autoComplete="username"
        value={email} onChange={(e) => setEmail(e.target.value)} />
      <input style={F} type="password" placeholder="Password" autoComplete="current-password"
        value={password} onChange={(e) => setPassword(e.target.value)} />
      <button className="ep-btn primary" disabled={busy || !email || !password}>
        {busy ? "Signing in…" : "Sign in"}
      </button>
      {error && <div style={{ marginTop: 12, fontSize: 13, color: "var(--ep-rosewood)" }}>{error}</div>}
    </form>
  );
}

function Pipeline() {
  const [agents, setAgents] = useState([]);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    api.get("/staff/agents").then((d) => setAgents(d.agents || []))
      .catch((e) => setError(e.message || "Could not load agents"));
  }, []);

  if (error) return <div style={{ ...CARD, color: "var(--ep-rosewood)" }}>{error}</div>;

  return (
    <>
      <p style={{ font: "400 15px/1.6 var(--ep-font-body)", color: "var(--ep-fg-muted)",
        maxWidth: "62ch", marginBottom: 24 }}>
        Open an agent to see exactly what they were sent, or preview the app as they see it.
        The preview is <strong>read-only</strong> — you can look at every screen, including their
        agreement, but nothing can be signed or submitted on their behalf.
      </p>
      {agents.map((a) => (
        <div key={a.id} style={CARD}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline",
            gap: 16, flexWrap: "wrap" }}>
            <div>
              <div style={{ font: "400 17px/1.3 var(--ep-font-body)", color: "var(--ep-origin)" }}>
                {a.name || a.email}
              </div>
              <div style={{ fontSize: 12, color: "var(--ep-fg-muted)", marginTop: 2 }}>
                {a.email} · {stageOf(a)}
                {/* A walkthrough sits at "gate open, not finished" indefinitely,
                    which reads exactly like a stalled recruit. Say which it is. */}
                {a.is_test && (
                  <span style={{ marginLeft: 8, padding: "1px 6px", borderRadius: 2,
                    background: "var(--ep-sunset)", color: "#FFF5E4", fontSize: 10,
                    letterSpacing: "0.06em", textTransform: "uppercase" }}>Test</span>
                )}
              </div>
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button className="ep-btn ghost sm" onClick={() => setOpen(open === a.id ? null : a.id)}>
                {open === a.id ? "Hide" : "What they were sent"}
              </button>
              <ViewAsButton agent={a} />
            </div>
          </div>
          {open === a.id && <AgentDetail agentId={a.id} />}
        </div>
      ))}
      {!agents.length && <div style={CARD}>No recruits on file.</div>}
    </>
  );
}

function stageOf(a) {
  if (a.onboarding_completed_at) return "finished";
  if (a.gate_opened_at) return "gate open — not finished";
  if (a.ica_completed_at) return "ICA signed, awaiting welcome form";
  if (a.welcome_done) return "welcome form in, awaiting ICA";
  return "invited";
}

function ViewAsButton({ agent }) {
  const [busy, setBusy] = useState(false);
  return (
    <button className="ep-btn primary sm" disabled={busy}
      onClick={async () => {
        // Named plainly. "View as" reads like sign-in-as, and somebody should know
        // which of those they are about to do before they click.
        if (!window.confirm(
          `Preview the onboarding app as ${agent.name || agent.email}?\n\n`
          + "You'll see every screen exactly as they do. It is READ-ONLY — nothing can be "
          + "signed or submitted on their behalf, and the preview is recorded against your name.")) return;
        setBusy(true);
        try {
          await api.post(`/staff/view-as/${agent.id}`, {});
          window.location.href = "/";
        } catch (e) {
          alert(e.message || "Could not start the preview");
          setBusy(false);
        }
      }}>
      {busy ? "Opening…" : <>Preview as agent <IcArrow /></>}
    </button>
  );
}

function AgentDetail({ agentId }) {
  const [d, setD] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    api.get(`/staff/agents/${agentId}`).then(setD).catch((e) => setError(e.message));
  }, [agentId]);

  if (error) return <div style={{ marginTop: 16, color: "var(--ep-rosewood)", fontSize: 13 }}>{error}</div>;
  if (!d) return <div style={{ marginTop: 16, fontSize: 13, color: "var(--ep-fg-muted)" }}>Loading…</div>;

  // Two independent records say whether an ICA exists: the envelope in
  // signed_documents, and the ica_completed_at stamp on the intake. They normally
  // agree, because the stamp is written by the envelope's own webhook. When they
  // DISAGREE, saying either one out loud would be a confident falsehood — and this
  // panel exists to be the thing Agent Services can repeat to an agent. So the
  // third state says the records disagree and names where to go and check.
  const stamped = !!d.intake?.ica_completed_at;
  const state = d.icaSent ? "sent" : stamped ? "conflict" : "none";
  const tone = { sent:     { bg: "rgba(204,207,129,0.18)", br: "var(--ep-avocado)" },
                 conflict: { bg: "#FDF2E9",                br: "var(--ep-rosewood)" },
                 none:     { bg: "#FDF2E9",                br: "var(--ep-sunset)" } }[state];

  return (
    <div style={{ marginTop: 18, borderTop: "1px solid var(--ep-border)", paddingTop: 16 }}>
      {/* The answer to the question that prompted this portal, said out loud
          rather than left as an empty list somebody has to interpret. */}
      <div style={{ padding: "10px 14px", marginBottom: 16,
        background: tone.bg, border: `1px solid ${tone.br}`,
        font: "400 14px/1.5 var(--ep-font-body)" }}>
        {state === "sent" &&
          <><IcCheck size={12}/> An ICA has been sent to this agent — see the documents below.</>}
        {state === "none" &&
          "No ICA has ever been sent to this agent by this app. If they are asking about "
          + "one, it came from somewhere else."}
        {state === "conflict" &&
          "The records disagree: this agent is marked as having signed an ICA, but no "
          + "envelope is on file here. Search BoldSign for their email before telling "
          + "them anything — do not say either way from this screen."}
      </div>

      <Section title="Documents">
        {d.documents.length
          ? d.documents.map((doc) => (
              <Row key={doc.envelope_id}
                left={`${doc.doc_type} · ${doc.status}`}
                right={fmt(doc.created_at)}
                sub={doc.drive_file_url
                  ? <a href={doc.drive_file_url} target="_blank" rel="noreferrer"
                      style={{ color: "var(--ep-sunset)" }}>Open the signed PDF →</a>
                  : doc.subject}/>
            ))
          : <Empty>No documents on file.</Empty>}
      </Section>

      <Section title="Email delivered">
        {d.mail.length
          ? d.mail.map((m, i) => (
              <Row key={i} left={m.kind || m.record_type} right={fmt(m.created_at)} sub={m.recipient}/>
            ))
          : <Empty>No delivery records. Only the Postmark path is logged here —
              staff and partner mail goes over SMTP and does not appear.</Empty>}
      </Section>
    </div>
  );
}

const Section = ({ title, children }) => (
  <div style={{ marginBottom: 18 }}>
    <div className="ep-eyebrow" style={{ fontSize: 10, color: "var(--ep-fg-muted)", marginBottom: 8 }}>
      {title}
    </div>
    {children}
  </div>
);
const Row = ({ left, right, sub }) => (
  <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 12,
    padding: "6px 0", borderBottom: "1px solid var(--ep-border)" }}>
    <div>
      <div style={{ fontSize: 13, color: "var(--ep-fg)" }}>{left}</div>
      {sub && <div style={{ fontSize: 11, color: "var(--ep-fg-muted)", overflowWrap: "anywhere" }}>{sub}</div>}
    </div>
    <div style={{ fontSize: 11, color: "var(--ep-fg-muted)" }}>{right}</div>
  </div>
);
const Empty = ({ children }) => (
  <div style={{ fontSize: 12, color: "var(--ep-fg-muted)", fontStyle: "italic" }}>{children}</div>
);
const fmt = (t) => (t ? new Date(t).toLocaleString() : "");
